<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import PlanSelectionModal from './PlanSelectionModal.vue';

export default {
  name: 'LicenseBlockedOverlay',
  components: {
    PlanSelectionModal,
  },
  computed: {
    ...mapGetters('license', [
      'shouldShowPaywall',
      'isLicenseBlocked',
      'arePlansUnavailable',
      'installationIdentifier',
    ]),
    ...mapGetters({
      globalConfig: 'globalConfig/get',
    }),
    isBlocked() {
      return this.shouldShowPaywall;
    },
    showSupportScreen() {
      return this.isLicenseBlocked && this.arePlansUnavailable;
    },
    showPlanModal() {
      return (
        this.isBlocked && (!this.arePlansUnavailable || !this.isLicenseBlocked)
      );
    },
    blockReason() {
      return this.$store.state.license.reason || 'LICENSE_BLOCKED';
    },
    normalizedBlockState() {
      const reason = this.blockReason.toString();
      if (['LICENSE_EXPIRED', 'TRIAL_EXPIRED'].includes(reason)) {
        return 'EXPIRED';
      }
      if (
        ['DOMAIN_MISMATCH', 'INSTALLATION_MISMATCH', 'MACHINE_MISMATCH'].some(
          code => reason.includes(code)
        )
      ) {
        return 'INSTALLATION_MISMATCH';
      }
      if (reason.includes('LICENSE_NOT_FOUND')) return 'LICENSE_NOT_FOUND';
      if (reason.includes('LICENSE_SUSPENDED')) return 'LICENSE_SUSPENDED';
      if (reason.includes('NETWORK')) return 'VALIDATION_UNAVAILABLE';
      return 'GENERIC';
    },
    isInstallationMismatch() {
      return this.normalizedBlockState === 'INSTALLATION_MISMATCH';
    },
    isRecoverableValidationIssue() {
      return ['INSTALLATION_MISMATCH', 'LICENSE_NOT_FOUND', 'GENERIC'].includes(
        this.normalizedBlockState
      );
    },
    blockTitle() {
      return this.replaceInstallationName(this.licenseStateText('TITLE'));
    },
    blockDescription() {
      return this.replaceInstallationName(this.licenseStateText('DESCRIPTION'));
    },
    blockHint() {
      return this.replaceInstallationName(this.licenseStateText('HINT'));
    },
    supportEmail() {
      return this.globalConfig?.supportEmail || 'suporte@chatplan.com.br';
    },
    brandName() {
      return this.globalConfig?.installationName || 'Chatplan';
    },
  },
  mounted() {
    if (this.isBlocked) {
      this.$store.dispatch('license/fetchPlans');
    }
  },
  methods: {
    async retryValidation() {
      await this.$store.dispatch('license/validateLicense');
      if (!this.$store.state.license.blocked) {
        window.location.reload();
      }
    },
    async copyInstallationIdentifier() {
      if (!this.installationIdentifier) return;
      await copyTextToClipboard(this.installationIdentifier);
      useAlert(this.$t('LICENSE.PAYWALL.COPY_IDENTIFIER_SUCCESS'));
    },
    licenseStateText(field) {
      const states = {
        EXPIRED: {
          TITLE: this.$t('LICENSE.PAYWALL.STATES.EXPIRED.TITLE'),
          DESCRIPTION: this.$t('LICENSE.PAYWALL.STATES.EXPIRED.DESCRIPTION'),
          HINT: this.$t('LICENSE.PAYWALL.STATES.EXPIRED.HINT'),
        },
        INSTALLATION_MISMATCH: {
          TITLE: this.$t('LICENSE.PAYWALL.STATES.INSTALLATION_MISMATCH.TITLE'),
          DESCRIPTION: this.$t(
            'LICENSE.PAYWALL.STATES.INSTALLATION_MISMATCH.DESCRIPTION'
          ),
          HINT: this.$t('LICENSE.PAYWALL.STATES.INSTALLATION_MISMATCH.HINT'),
        },
        LICENSE_NOT_FOUND: {
          TITLE: this.$t('LICENSE.PAYWALL.STATES.LICENSE_NOT_FOUND.TITLE'),
          DESCRIPTION: this.$t(
            'LICENSE.PAYWALL.STATES.LICENSE_NOT_FOUND.DESCRIPTION'
          ),
          HINT: this.$t('LICENSE.PAYWALL.STATES.LICENSE_NOT_FOUND.HINT'),
        },
        LICENSE_SUSPENDED: {
          TITLE: this.$t('LICENSE.PAYWALL.STATES.LICENSE_SUSPENDED.TITLE'),
          DESCRIPTION: this.$t(
            'LICENSE.PAYWALL.STATES.LICENSE_SUSPENDED.DESCRIPTION'
          ),
          HINT: this.$t('LICENSE.PAYWALL.STATES.LICENSE_SUSPENDED.HINT'),
        },
        VALIDATION_UNAVAILABLE: {
          TITLE: this.$t('LICENSE.PAYWALL.STATES.VALIDATION_UNAVAILABLE.TITLE'),
          DESCRIPTION: this.$t(
            'LICENSE.PAYWALL.STATES.VALIDATION_UNAVAILABLE.DESCRIPTION'
          ),
          HINT: this.$t('LICENSE.PAYWALL.STATES.VALIDATION_UNAVAILABLE.HINT'),
        },
        GENERIC: {
          TITLE: this.$t('LICENSE.PAYWALL.STATES.GENERIC.TITLE'),
          DESCRIPTION: this.$t('LICENSE.PAYWALL.STATES.GENERIC.DESCRIPTION'),
          HINT: this.$t('LICENSE.PAYWALL.STATES.GENERIC.HINT'),
        },
      };
      return (
        states[this.normalizedBlockState]?.[field] || states.GENERIC[field]
      );
    },
    replaceInstallationName(text) {
      if (!text) return text;
      const installationName =
        this.globalConfig?.installationName || this.brandName;
      return text
        .replace(/Chatwoot/g, installationName)
        .replace(/ChatPlan/g, installationName)
        .replace(/Chatplan/g, installationName);
    },
  },
};
</script>

<template>
  <div v-show="isBlocked">
    <!-- Tela informativa quando Aegis/planos indisponíveis -->
    <div
      v-if="showSupportScreen"
      id="chatplan-license-paywall"
      class="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/80 backdrop-blur-sm"
    >
      <div
        class="mx-4 w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-2xl dark:bg-slate-800"
      >
        <div
          class="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30"
        >
          <svg
            class="h-8 w-8 text-amber-600 dark:text-amber-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
        </div>
        <h2 class="mb-2 text-xl font-bold text-slate-900 dark:text-white">
          {{ blockTitle }}
        </h2>
        <p class="mb-6 text-sm text-slate-600 dark:text-slate-300">
          {{ blockDescription }}
        </p>
        <div
          v-if="installationIdentifier && isInstallationMismatch"
          class="mb-4 rounded-lg bg-slate-50 p-4 text-left dark:bg-slate-700/50"
        >
          <p
            class="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400"
          >
            {{ $t('LICENSE.PAYWALL.INSTALLATION_IDENTIFIER') }}
          </p>
          <p
            class="break-all font-mono text-xs text-slate-700 dark:text-slate-200"
          >
            {{ installationIdentifier }}
          </p>
        </div>
        <div class="mb-4 flex flex-col gap-2">
          <button
            v-if="installationIdentifier && isInstallationMismatch"
            class="flex w-full items-center justify-center gap-2 rounded-lg bg-woot-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-woot-600"
            @click="copyInstallationIdentifier"
          >
            <span class="i-lucide-copy size-4" />
            {{ $t('LICENSE.PAYWALL.COPY_IDENTIFIER') }}
          </button>
          <button
            v-if="isRecoverableValidationIssue"
            class="flex w-full items-center justify-center gap-2 rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:border-woot-500 hover:text-woot-600 dark:border-slate-600 dark:text-slate-200"
            @click="retryValidation"
          >
            <span class="i-lucide-refresh-cw size-4" />
            {{ $t('LICENSE.PAYWALL.RETRY_VALIDATION') }}
          </button>
        </div>
        <div class="rounded-lg bg-slate-50 p-4 text-left dark:bg-slate-700/50">
          <p
            class="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400"
          >
            {{ $t('LICENSE.BLOCKED.NEED_HELP') }}
          </p>
          <p class="text-sm text-slate-700 dark:text-slate-200">
            {{ blockHint || $t('LICENSE.BLOCKED.CONTACT_SUPPORT') }}
          </p>
          <a
            :href="`mailto:${supportEmail}`"
            class="mt-1 block text-sm font-medium text-woot-500 hover:text-woot-600"
          >
            {{ supportEmail }}
          </a>
        </div>
        <p class="mt-4 text-xs text-slate-400 dark:text-slate-500">
          {{ $t('LICENSE.BLOCKED.AUTO_RECONNECT') }}
        </p>
      </div>
    </div>

    <!-- Modal de seleção de planos quando disponível -->
    <PlanSelectionModal v-else-if="showPlanModal" />
  </div>
</template>
